import {
  CalendarDayGroup,
  CalendarEvent,
  CalendarMonthDay,
  CalendarTimeSlot,
  CalendarWeekDay,
} from '../models/calendar.types';

/**
 * Assure la casse de phrase (Sentence case) en français :
 * Seule la première lettre est en majuscule, tout le reste en minuscules.
 */
export function toSentenceCase(text: string): string {
  if (!text) return '';
  const trimmed = text.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Normalise une date à minuit UTC/local pour comparaison de dates pures.
 */
export function isSameDay(d1: Date, d2: Date): boolean {
  return (
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate()
  );
}

/**
 * Calcule le premier jour de la semaine (lundi si firstDayOfWeek=1, dimanche si 0).
 */
export function getStartOfWeek(date: Date, firstDayOfWeek: 1 | 0 = 1): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = firstDayOfWeek === 1 ? (day === 0 ? -6 : 1 - day) : -day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Calcule le lundi de la semaine contenant la date passée en paramètre.
 */
export function getMondayOfWeek(date: Date): Date {
  return getStartOfWeek(date, 1);
}

/**
 * Génère la grille des jours du mois (6 semaines)
 * pour un affichage stable et sans saut de mise en page.
 */
export function generateMonthGrid<T = unknown>(
  currentDate: Date,
  events: CalendarEvent<T>[] = [],
  firstDayOfWeek: 1 | 0 = 1,
  showWeekends = true,
): CalendarMonthDay<T>[] {
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Premier jour du mois
  const firstDayOfMonth = new Date(year, month, 1);
  // Premier jour affiché dans la grille
  const startDate = getStartOfWeek(firstDayOfMonth, firstDayOfWeek);

  const days: CalendarMonthDay<T>[] = [];
  const currentPointer = new Date(startDate);

  // 6 semaines x 7 jours
  for (let i = 0; i < 42; i++) {
    const dayDate = new Date(currentPointer);
    const dayOfWeek = dayDate.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (showWeekends || !isWeekend) {
      const dayNumber = dayDate.getDate();
      const isCurrentMonth = dayDate.getMonth() === month;
      const isToday = isSameDay(dayDate, today);

      // Événements du jour
      const dayEvents = events
        .filter((e) => {
          const start = new Date(e.startAt);
          return isSameDay(start, dayDate);
        })
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

      days.push({
        date: dayDate,
        isoDate: dayDate.toISOString(),
        dayNumber,
        isCurrentMonth,
        isToday,
        isWeekend,
        events: dayEvents,
      });
    }

    currentPointer.setDate(currentPointer.getDate() + 1);
  }

  return days;
}

/**
 * Génère les jours de la semaine courante (selon le premier jour et l'option week-end).
 */
export function generateWeekDays(
  currentDate: Date,
  firstDayOfWeek: 1 | 0 = 1,
  showWeekends = true,
): CalendarWeekDay[] {
  const today = new Date();
  const startDay = getStartOfWeek(currentDate, firstDayOfWeek);
  const days: CalendarWeekDay[] = [];

  const dayNamesFull = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

  for (let i = 0; i < 7; i++) {
    const d = new Date(startDay);
    d.setDate(startDay.getDate() + i);
    const dayOfWeek = d.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    if (showWeekends || !isWeekend) {
      days.push({
        date: d,
        isoDate: d.toISOString(),
        dayName: dayNamesFull[dayOfWeek],
        dayNumber: d.getDate(),
        isToday: isSameDay(d, today),
      });
    }
  }

  return days;
}

/**
 * Génère les créneaux horaires de la journée pour la vue semaine (ex: 06:00 à 22:00).
 */
export function generateTimeSlots(startHour = 6, endHour = 22): CalendarTimeSlot[] {
  const slots: CalendarTimeSlot[] = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    const label = `${String(hour).padStart(2, '0')}:00`;
    slots.push({ hour, label });
  }
  return slots;
}

/**
 * Regroupe les événements chronologiquement par jour pour la vue liste/agenda.
 */
export function groupEventsByDay<T = unknown>(
  events: CalendarEvent<T>[] = [],
  filterFromDate?: Date,
  filterToDate?: Date,
): CalendarDayGroup<T>[] {
  const today = new Date();
  const fromTime = filterFromDate ? new Date(filterFromDate).setHours(0, 0, 0, 0) : null;
  const toTime = filterToDate ? new Date(filterToDate).setHours(23, 59, 59, 999) : null;

  // Filtrer par plage de date si demandée
  const filtered = events.filter((e) => {
    const time = new Date(e.startAt).getTime();
    if (fromTime !== null && time < fromTime) return false;
    if (toTime !== null && time > toTime) return false;
    return true;
  });

  // Trier par date
  filtered.sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

  const groupsMap = new Map<string, { date: Date; events: CalendarEvent<T>[] }>();

  for (const event of filtered) {
    const d = new Date(event.startAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    const existing = groupsMap.get(key);
    if (!existing) {
      groupsMap.set(key, { date: d, events: [event] });
    } else {
      existing.events.push(event);
    }
  }

  const groups: CalendarDayGroup<T>[] = [];
  for (const [, val] of groupsMap.entries()) {
    const isToday = isSameDay(val.date, today);
    const dateLabel = formatGroupDateHeader(val.date);

    groups.push({
      date: val.date,
      dateLabel,
      isToday,
      events: val.events,
    });
  }

  return groups;
}

/**
 * Formate le titre du mois et de l'année (ex: "Octobre 2026") en casse de phrase.
 */
export function formatMonthTitle(date: Date, locale = 'fr-FR'): string {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
  return toSentenceCase(formatter.format(date));
}

/**
 * Formate l'en-tête de la semaine (ex: "28 septembre – 4 octobre 2026") en casse de phrase.
 */
export function formatWeekTitle(
  currentDate: Date,
  locale = 'fr-FR',
  firstDayOfWeek: 1 | 0 = 1,
  showWeekends = true,
): string {
  const startDayDate = getStartOfWeek(currentDate, firstDayOfWeek);
  const endDayDate = new Date(startDayDate);
  const numDays = showWeekends ? 6 : 4;
  endDayDate.setDate(startDayDate.getDate() + numDays);

  const startDay = startDayDate.getDate();
  const endDay = endDayDate.getDate();

  const startMonth = new Intl.DateTimeFormat(locale, { month: 'long' }).format(startDayDate);
  const endMonth = new Intl.DateTimeFormat(locale, { month: 'long' }).format(endDayDate);
  const year = endDayDate.getFullYear();

  if (startDayDate.getMonth() === endDayDate.getMonth()) {
    return toSentenceCase(`${startDay} – ${endDay} ${endMonth} ${year}`);
  }

  return toSentenceCase(`${startDay} ${startMonth} – ${endDay} ${endMonth} ${year}`);
}

/**
 * Formate l'en-tête de date d'un groupe en vue liste (ex: "Lundi 5 octobre 2026").
 */
export function formatGroupDateHeader(date: Date, locale = 'fr-FR'): string {
  const today = new Date();
  if (isSameDay(date, today)) {
    const full = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
    return `Aujourd'hui (${toSentenceCase(full)})`;
  }

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  if (isSameDay(date, tomorrow)) {
    const full = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date);
    return `Demain (${toSentenceCase(full)})`;
  }

  const formatted = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);

  return toSentenceCase(formatted);
}

/**
 * Formate une plage horaire (ex: "14:00 – 15:30").
 */
export function formatTimeRange(startAt: string | Date, endAt: string | Date): string {
  const s = typeof startAt === 'string' ? new Date(startAt) : startAt;
  const e = typeof endAt === 'string' ? new Date(endAt) : endAt;

  const pad = (n: number) => String(n).padStart(2, '0');
  const startStr = `${pad(s.getHours())}:${pad(s.getMinutes())}`;
  const endStr = `${pad(e.getHours())}:${pad(e.getMinutes())}`;

  return `${startStr} – ${endStr}`;
}
